import i18n from '@/core/i18n';
import { BaseStatus, StatusConfig } from '@/modules/common';
import { createStatusOptionsMapper } from '@/modules/common/mappers/status-options.mapper';
import { BillStatus } from '@/modules/finance/bills/types';

export class BillStatusInProgress implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-chart-3 text-chart-3',
    };
  }
}

export class BillStatusError implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-destructive text-destructive',
    };
  }
}
/**
 * Bill status classes that implement the BaseStatus interface.
 * Each status knows how to configure itself for the StatusBadge component.
 */
export class BillStatusOpen implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-cyan-600 text-cyan-600',
    };
  }
}

export class BillStatusOverdue implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-destructive text-destructive',
    };
  }
}

export class BillStatusPaid implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-success text-success',
    };
  }
}

export class BillStatusUnknown implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-muted-foreground text-muted-foreground',
    };
  }
}

// Factory function to create status instances based on enum values
export const createBillStatus = (status: string): BaseStatus => {
  switch (status) {
    case BillStatus.Open:
      return new BillStatusOpen();
    case BillStatus.Overdue:
      return new BillStatusOverdue();
    case BillStatus.Paid:
      return new BillStatusPaid();
    case BillStatus.InProgress:
      return new BillStatusInProgress();
    case BillStatus.Error:
      return new BillStatusError();
    default:
      return new BillStatusUnknown(); // Default fallback
  }
};

/**
 * Get localized status text for Bill status.
 */
export const getBillStatusText = (status: BillStatus): string => {
  switch (status) {
    case BillStatus.Open:
      return i18n.t('authenticated.bills:status.open');
    case BillStatus.Overdue:
      return i18n.t('authenticated.bills:status.overdue');
    case BillStatus.Paid:
      return i18n.t('authenticated.bills:status.paid');
    case BillStatus.InProgress:
      return i18n.t('authenticated.bills:status.inProgress');
    case BillStatus.Error:
      return i18n.t('authenticated.bills:status.error');
    default:
      return i18n.t('authenticated.bills:status.unknown');
  }
};

export const getBillStatusColor = (status: BillStatus): string => {
  switch (status) {
    case BillStatus.Open:
      return 'cyan-600';
    case BillStatus.Overdue:
      return 'destructive';
    case BillStatus.Paid:
      return 'success';
    case BillStatus.InProgress:
      return 'chart-3';
    case BillStatus.Error:
      return 'destructive';
    default:
      return 'muted-foreground';
  }
};

/**
 * Preconfigured mapper for bill status options.
 * Maps an array of BillStatus values to option objects with label, value, and color.
 */
export const getBillStatusOptions = createStatusOptionsMapper<BillStatus>({
  getStatusColor: getBillStatusColor,
  getStatusText: getBillStatusText,
});
